import { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API } from '@/Services/apiConfig';
import { reportError } from '@/Services/errorReporter';

interface Slot {
    date: string;
    period: string;
    fulfilment: string;
}

interface MenuChangedData {
    date: string;
    period: string | null;
    fulfilment: string | null;
}

function slotsEqual(a: Slot | null, b: Slot | null): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.date === b.date && a.period === b.period && a.fulfilment === b.fulfilment;
}

export const useMenuSSE = (slot: Slot | null) => {
    const [connected, setConnected] = useState(false);
    const eventSourceRef = useRef<EventSource | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectAttemptsRef = useRef(0);
    const isInitialMount = useRef(true);
    const slotRef = useRef<Slot | null>(null);
    const hasDisconnectedRef = useRef(false);
    const queryClient = useQueryClient();

    const lastEventIdRef = useRef<string | null>(null);

    const connect = useCallback(() => {
        const currentSlot = slotRef.current;
        if (!currentSlot) return;

        if (eventSourceRef.current) {
            return;
        }

        const url = new URL(API.MENU_STREAM, window.location.origin);
        if (lastEventIdRef.current) {
            url.searchParams.set('last_event_id', lastEventIdRef.current);
        }

        const es = new EventSource(url.pathname + url.search);
        eventSourceRef.current = es;

        es.addEventListener('menu_changed', (event: MessageEvent) => {
            if (event.lastEventId) {
                lastEventIdRef.current = event.lastEventId;
            }
            try {
                const data: MenuChangedData = JSON.parse(event.data);
                const s = slotRef.current;
                if (
                    s &&
                    data.date === s.date &&
                    (data.period === null ||
                        data.fulfilment === null ||
                        (data.period === s.period && data.fulfilment === s.fulfilment))
                ) {
                    queryClient.invalidateQueries({ queryKey: ['menu'] });
                }
            } catch (err) {
                reportError({
                    message: err instanceof Error ? err.message : 'Failed to parse SSE data',
                    source: 'useMenuSSE.menu_changed',
                    stack: err instanceof Error ? err.stack : undefined,
                });
            }
        });

        es.onopen = () => {
            setConnected(true);
            reconnectAttemptsRef.current = 0;
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            if (!isInitialMount.current && hasDisconnectedRef.current) {
                queryClient.invalidateQueries({ queryKey: ['menu'] });
                hasDisconnectedRef.current = false;
            }
            isInitialMount.current = false;
        };

        es.onerror = () => {
            setConnected(false);
            hasDisconnectedRef.current = true;
            es.close();
            if (eventSourceRef.current === es) {
                eventSourceRef.current = null;
            }

            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 60000);
            reconnectAttemptsRef.current++;

            reconnectTimeoutRef.current = setTimeout(() => {
                connect();
            }, delay);
        };
    }, [queryClient]);

    useEffect(() => {
        if (!slot) return;

        if (!slotsEqual(slotRef.current, slot)) {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
            reconnectAttemptsRef.current = 0;
            slotRef.current = slot;
        }

        connect();

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [slot, connect]);

    return { connected };
};
